// The tool rail: what a drag does, down the right edge (PLAN-UI §28.3, U5;
// screen-mock `#rail`).
//
// **Persistent chrome, because the question is always live.** What a drag on
// the board means is not a property of anything selected, so the answer cannot
// live in a panel that comes and goes with a selection — it was in the shelf's
// top strip, which is the one row of the dock that never changed, and this is
// that row given an edge of its own. Always visible, always answering "what
// happens if I drag" without a trial drag to find out.
//
// **Watching hides rather than disables.** Build and Discard issue commands and a
// watched game has no seat to issue them from, so they are two buttons that
// could never do anything. "A control is disabled, never removed" (§28.6) is
// about controls that *flicker* under the pointer; a watched game is a whole
// seat and is fixed for its whole length, which is the exception that rule
// carries.
//
// **Discard is a tile of its own, not a context of Build** — the strip made it a
// sub-row that appeared once you were building, which cost a click on the
// rarer half and made the row's width depend on the live tool. See `tool.ts`.
//
// **The camera's tiles hang under the tools, past a break.** They are actions
// rather than modes — a quarter turn, the tilt, a fit — and they are here
// because every camera action has to be reachable by a finger: the pause menu
// was two clicks away from the board it acts on, and the keys that mirror these
// only exist for people who have a keyboard. See `viewActions.ts`.
//
// Explicit props, no store reads: the models are `railTools` and `viewActions`,
// and this draws them.

import type { ReactNode } from "react";

import { railTools, type Tool } from "./tool";
import type { ViewAction } from "./viewActions";
import { HoverCard, Icon, styled, Tile, TileGlyph, useChromeRect } from "./kit";

export interface ToolRailProps {
  tool: Tool;
  onTool(tool: Tool): void;
  /** Two machines are playing each other and nobody holds a seat. */
  watching: boolean;
  /**
   * The camera's own tiles — turn, tilt, fit (`viewActions.ts`).
   *
   * **Every camera action is reachable by a finger**, which is the whole of why
   * they left the pause menu: keys are accelerators, and a touch screen has
   * none. Handed in as a list with one callback rather than as four props, so
   * a fifth action is a row in the model and nothing here.
   */
  actions: ViewAction[];
  onAction(id: ViewAction["id"]): void;
  /** Anything that hangs under the tools (U5-B). */
  children?: ReactNode;
}

// Static: nothing here moves per frame, so the whole column is one emotion
// class rather than a `style` object rewritten on every render.
const Column = styled("div")(({ theme }) => ({
  position: "absolute",
  right: 14,
  top: "50%",
  // Not `-50%`: the mock's own number, and it also drops the rail's top clear
  // of the turn row and the objectives stacked under it — measured at 1280x720
  // in a run, the rail's top edge sits well below the objective panel's foot.
  transform: "translateY(-42%)",
  zIndex: theme.kit.z.chrome,
  display: "flex",
  flexDirection: "column",
  gap: 7
}));

/**
 * What hangs under the tools, and the break above it.
 *
 * The tools answer "what does a drag do" and what hangs below answers "what is
 * my army doing without me" — two subjects, so the column says so with space
 * rather than with a rule, which at 46px wide would read as a divider between
 * two halves of one list. **The break is on the wrapper and the wrapper hides
 * when empty**: a parent cannot see whether a child rendered anything — so `:empty` is what
 * keeps the rail's optical centre from shifting 4px for an invisible gap.
 * (The popover portals to `body`, so an open one does not fill it either.)
 */
const Under = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 7,
  marginTop: 1,
  "&:empty": { display: "none" }
});

/**
 * The break between what a drag means and where the camera is pointing.
 *
 * Space rather than a rule, for the reason `Under` uses space: at 46px wide a
 * line reads as a divider between two halves of one list, and these are two
 * subjects — what the pointer does, and what the view does.
 */
const Break = styled("div")(({ theme }) => ({ height: theme.kit.space.xs }));

export function ToolRail({
  tool,
  onTool,
  watching,
  actions,
  onAction,
  children
}: ToolRailProps) {
  const root = useChromeRect("tool-rail", "right");

  return (
    <Column ref={root} data-testid="tool-rail">
      {railTools({ tool, watching }).map((entry) => (
        // The word and the sentence, on the kit's own surface rather than in a
        // browser tooltip (designer ruling, U6 review). An off tile's whole job
        // is to say *why*, which is why `Tile` keeps it hoverable rather than
        // taking the native `disabled` — and why the card is on the outside of
        // that decision: it explains a live tile and a dead one the same way.
        // The sentence is still the only place either gesture is spelled out
        // until U7's pill.
        <HoverCard
          key={entry.id}
          placement="left"
          title={entry.label}
          card={entry.reason ?? entry.hint}
          data-testid={`tool-card-${entry.id}`}
        >
          <Tile
            variant="rail"
            // These ids are API: the smokes have clicked them since the tool
            // bar was a row at the bottom of the window.
            data-testid={`tool-${entry.id}`}
            armed={entry.active}
            aria-pressed={entry.active}
            // The tile's only name. A glyph is text and would announce itself
            // as "♟"; an icon announces nothing at all — so the word is said
            // here once, for both, rather than twice in two mechanisms.
            aria-label={entry.label}
            disabled={entry.reason !== null}
            onClick={() => onTool(entry.id)}
          >
            {entry.icon ? (
              <Icon name={entry.icon} size={20} />
            ) : (
              <TileGlyph small>{entry.glyph}</TileGlyph>
            )}
          </Tile>
        </HoverCard>
      ))}

      <Break />
      {actions.map((action) => (
        // The word and the sentence, on the kit's own surface — the same one
        // hover per tile the tools take, and the sentence carries the key as
        // well, because a key nobody is told about is a key nobody presses.
        <HoverCard
          key={action.id}
          placement="left"
          title={action.label}
          card={action.hint}
          data-testid={`view-card-${action.id}`}
        >
          <Tile
            variant="rail"
            // API, and two of them are older than this rail: `view-rotate` and
            // `view-fit` were clicked in the pause menu.
            data-testid={action.id}
            armed={action.on}
            // Only where there is a state to be in. A button that announces
            // itself as a toggle without being one is a lie to a reader.
            aria-pressed={action.on}
            // The tile's only name: an icon announces nothing at all.
            aria-label={action.label}
            onClick={() => onAction(action.id)}
          >
            <Icon name={action.icon} size={20} />
          </Tile>
        </HoverCard>
      ))}

      <Under>{children}</Under>
    </Column>
  );
}
